
import './Nudge.scss';

import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import Text from '@ulta/core/components/Text/Text';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { handleRedirect } from '@ulta/core/utils/handleLocation/handleLocation';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage';

import { isAuthButton } from '@ulta/utils/actionUtils/actionUtils';

import * as utils from './Nudge';

/**
 * Represents a Nudge component
 *
 * @method
 * @returns Nudge
 */
export const Nudge = forwardRef( ( props, ref ) => {
  const [showNudge, setShowNudge] = useState( false );
  const { title, action, message, closeAction, type } = props;

  const handleSetStorage = useCallback( utils.composeHandleSetStorage( { setStorage } ), [setStorage] );
  const handleCloseNudge = useCallback( utils.composeCloseNudge( { setShowNudge, handleSetStorage }, type ), [setShowNudge, handleSetStorage] );
  const handleShowNudge = useCallback( utils.composeShowNudge( { setShowNudge, getStorage, handleSetStorage }, type ), [setShowNudge, getStorage, handleSetStorage] );
  const handleRedirectNudgeAction = useCallback( ( e ) => {
    handleCloseNudge( e );
    handleRedirect( { url: action.url } );
  }, [handleCloseNudge, action] );

  if( ref !== null ){
    // eslint-disable-next-line no-param-reassign
    ref.current = { handleCloseNudge };
  }

  useEffect( () => {
    handleShowNudge();
  }, [] );

  if( !title || !showNudge || isServer() ){
    return null;
  }

  return (
    <div className={ type === 'mfa' ? 'Nudge Nudge_mfa' : 'Nudge' }
      role='tooltip'
      aria-label='sign in'
    >
      <div className='Nudge__wrapper'>
        <div className='Nudge__content'>
          <Text
            textStyle='body-3'
            htmlTag={ title?.htmlTag }
            color='black'
            fontWeight='bold'
          >
            { title?.text }
          </Text>
          <Markdown
            alignment={ message?.alignment }
            bodyStyle='body-3-noPadding'
            cmsMarkdownContent={ message?.body }
          />
          {
            isAuthButton( action ) &&
            <Link_Huge
              action={ action }
              className='Nudge__actionLink'
            >
              { action?.label }
            </Link_Huge>
          }
          {
            !isAuthButton( action ) &&
            <Button
              likeLink={ true }
              className='Nudge__action'
              action={ action }
              onClick={ action?.navigationType === 'redirect' ? handleRedirectNudgeAction : handleCloseNudge }
              label={ action?.label }
            />
          }
        </div>
        <div className='Nudge__arrow'></div>
        <Button
          className='Nudge__close'
          variant='unstyled'
          icon
          ariaLabel={ closeAction?.label }
          iconSize='lg'
          iconImage={ closeAction?.label }
          tertiary={ true }
          action={ closeAction }
          onClick={ handleCloseNudge }
        />
      </div>
    </div>
  );
} );

/**
 * Returns a function that closes the nudge and sets storage for 3 days.
 *
 * @param {object} methods - Object containing setShowNudge and handleSetStorage methods
 * @param {function} methods.setShowNudge - Method to set showNudge state
 * @param {function} methods.handleSetStorage - Method to set storage value
 *
 * @return {function} - Function that closes the nudge and sets storage
 */
export const composeCloseNudge = ( methods, type ) => () => {
  const { setShowNudge, handleSetStorage } = methods || {};

  if( !setShowNudge || !handleSetStorage ){
    return;
  }

  handleSetStorage();
  setShowNudge( false );
  if( type === 'mfa' ){
    setStorage( { key:  'MFA_Nudge', value: {}, secure: true } ) || {};
  }
};

/**
 * Show nudge if it has expired
 * @function composeShowNudge
 * @param {object} methods - Object containing setShowNudge, getStorage, and handleSetStorage methods
 * @param {function} methods.setShowNudge - Method to set showNudge state
 * @param {function} methods.getStorage - Method to retrieve storage value
 * @param {function} methods.handleSetStorage - Method to set storage value
 * @returns {function} Function to show nudge if it has expired
 */
export const composeShowNudge = ( methods, type ) => () => {
  const { setShowNudge, getStorage, handleSetStorage } = methods || {};

  if( !setShowNudge || !getStorage || !handleSetStorage ){
    return;
  }
  const isNudgeVisible =  !getStorage( { key: type === 'mfa' ? 'MFA_Nudge' : 'Nudge', secure: true } );
  setShowNudge( isNudgeVisible );
  handleSetStorage();
};

/**
 * Closes the nudge and sets storage to expire in 3 days
 * @param {object} methods - Object containing setStorage method
 * @param {function} methods.setTtlStorage - Method to set storage value
 * @returns {function} Function to close nudge and set storage
 */
export const composeHandleSetStorage = ( methods ) => () => {
  const { setStorage } = methods || {};

  if( !setStorage ){
    return;
  }
  const threeDays = 3 * 24 * 60 * 60 * 1000;
  setStorage( { key: 'Nudge', value: {}, ttl: threeDays, secure: true } ) || {};
};

/**
 * Property type definitions
 * @typedef Nudge
 * @type {object}
 * @param {Object} props - The props for the Nudge component.
 * @param {object} props.t - The title of the nudge.
 * @param {object} props.message - The message of the nudge.
 * @param {object} props.action - The action of the nudge.
 * @param {object} props.closeAction - The CloseAction of the nudge.
 * @returns {JSX.Element|null} The Nudge component or null if no title is provided.
 */
export const propTypes = {
  title: PropTypes.object,
  message: PropTypes.object,
  action: PropTypes.object,
  closeAction: PropTypes.object,
  type: PropTypes.oneOf( ['mfa'] )
};

Nudge.propTypes = propTypes;

Nudge.displayName = 'Nudge';

export default Nudge;
